import React, { useEffect, useState, useCallback } from "react";
import { fetchPersonById, updatePerson } from "../../../services/aro/personService";
import { updateCertificate, CertificatePatch } from "../../../services/aro/certificateService";
import { PersonDetail, Certificate, Snapshot } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import Field          from "../../shared/Field/Field";
import Toast          from "../../shared/Toast/Toast";
import { certStatusBadge, canReactivate, isNoLinkedinPause, isTerminal, stopReasonLabel } from "../../../utils/certStatus";
import { formatDateLong } from "../../../utils/formatters";
import { useAuth } from "../../../context/useAuth";
import { canAccessQuarantine, canEditLinkedin } from "../../../utils/quarantineAccess";
import "./PersonDetailPage.css";

interface Props {
  personId: string;
  onBack:   () => void;
  onSelectCertificate: (certificateId: number) => void;
}

interface PersonDraft {
  full_name:    string;
  linkedin_url: string;
}

interface CertDraft {
  certificate_name:        string;
  external_cert_id:        string;
  certificate_earned_date: string;
  tracking_end_date:       string;
  next_snapshot_due:       string;
  tracking_status:         "active" | "paused" | "stopped";
}

function toCertDraft(c: Certificate): CertDraft {
  return {
    certificate_name:        c.certificate_name        ?? "",
    external_cert_id:        c.external_cert_id        ?? "",
    certificate_earned_date: c.certificate_earned_date ?? "",
    tracking_end_date:       c.tracking_end_date       ?? "",
    next_snapshot_due:       c.next_snapshot_due       ?? "",
    tracking_status:         c.tracking_status === "job_changed" ? "stopped" : c.tracking_status,
  };
}

function scrapeStatusReasonLabel(reason: string | null): string | null {
  if (reason === "no_linkedin_url")      return "No LinkedIn URL";
  if (reason === "consecutive_failures") return "Consecutive scrape failures";
  return null;
}

export default function PersonDetailPage({ personId, onBack, onSelectCertificate }: Props) {
  const { email: changedBy } = useAuth();
  const [person,    setPerson]    = useState<PersonDetail | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [toast,     setToast]     = useState<{ msg: string; variant: "success" | "info" | "error" } | null>(null);

  // Person edit
  const [isEditing,  setIsEditing]  = useState(false);
  const [draft,      setDraft]      = useState<PersonDraft | null>(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState<string | null>(null);

  // Certificate inline edit
  const [editingCertId,  setEditingCertId]  = useState<number | null>(null);
  const [certDraft,      setCertDraft]      = useState<CertDraft | null>(null);
  const [certSaving,     setCertSaving]     = useState(false);
  const [certError,      setCertError]      = useState<string | null>(null);

  // Certificate expand (detail dropdown)
  const [expandedCertId, setExpandedCertId] = useState<number | null>(null);

  const toggleExpand = (id: number) =>
    setExpandedCertId((prev) => (prev === id ? null : id));

  // Reactivating a cert (separate loading state per cert)
  const [reactivatingId, setReactivatingId] = useState<number | null>(null);

  const loadPerson = useCallback(() => {
    return fetchPersonById(personId)
      .then((p) => setPerson(p))
      .catch((e) => setError(e.message));
  }, [personId]);

  useEffect(() => {
    setLoading(true);
    loadPerson().finally(() => setLoading(false));
  }, [loadPerson]);

  // ── Person edit ───────────────────────────────────────────
  const startEdit = () => {
    if (!person) return;
    setDraft({ full_name: person.full_name ?? "", linkedin_url: person.linkedin_url ?? "" });
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => { setIsEditing(false); setDraft(null); setSaveError(null); };

  const handleSave = async () => {
    if (!person || !draft) return;
    const hadLinkedIn = !!person.linkedin_url;
    const addingLinkedIn = !hadLinkedIn && !!draft.linkedin_url.trim();

    setSaving(true);
    setSaveError(null);
    try {
      const fields: { full_name?: string; linkedin_url?: string } = {};
      if (canAccessQuarantine(changedBy) && draft.full_name.trim() !== (person.full_name ?? "")) fields.full_name   = draft.full_name.trim()    || undefined;
      if (draft.linkedin_url.trim() !== (person.linkedin_url ?? "")) fields.linkedin_url = draft.linkedin_url.trim() || undefined;
      if (Object.keys(fields).length === 0) { setIsEditing(false); setDraft(null); return; }

      await updatePerson(person.id, fields, changedBy);
      await loadPerson();
      setIsEditing(false);
      setDraft(null);

      if (addingLinkedIn) {
        setToast({ msg: "LinkedIn URL saved — paused certificates have been reactivated.", variant: "success" });
      } else {
        setToast({ msg: "Profile updated.", variant: "success" });
      }
    } catch (e: any) {
      setSaveError(e.message ?? "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // ── Cert reactivate (quick action, no edit panel) ─────────
  const handleReactivate = async (certId: number) => {
    if (!person?.linkedin_url) {
      setToast({ msg: "Cannot reactivate — person has no LinkedIn URL. Add one first.", variant: "error" });
      return;
    }
    setReactivatingId(certId);
    try {
      await updateCertificate(certId, { changed_by: changedBy, tracking_status: "active" });
      await loadPerson();
      setToast({ msg: "Certificate reactivated.", variant: "success" });
    } catch (e: any) {
      setToast({ msg: e.message ?? "Failed to reactivate.", variant: "error" });
    } finally {
      setReactivatingId(null);
    }
  };

  // ── Certificate full edit ─────────────────────────────────
  const startCertEdit = (cert: Certificate) => {
    setEditingCertId(cert.id);
    setCertDraft(toCertDraft(cert));
    setCertError(null);
  };

  const cancelCertEdit = () => { setEditingCertId(null); setCertDraft(null); setCertError(null); };

  const handleCertSave = async () => {
    if (!person || !editingCertId || !certDraft) return;
    setCertSaving(true);
    setCertError(null);
    const wasReactivating = certDraft.tracking_status === "active" &&
      certificates.find((c) => c.id === editingCertId)?.tracking_status !== "active";
    try {
      const body: CertificatePatch = {
        changed_by:              changedBy,
        certificate_name:        certDraft.certificate_name        || undefined,
        external_cert_id:        certDraft.external_cert_id        || null,
        certificate_earned_date: certDraft.certificate_earned_date || undefined,
        tracking_end_date:       certDraft.tracking_end_date       || undefined,
        next_snapshot_due:       certDraft.next_snapshot_due       || null,
        tracking_status:         certDraft.tracking_status,
      };
      await updateCertificate(editingCertId, body);
      await loadPerson();
      setEditingCertId(null);
      setCertDraft(null);
      setToast({
        msg: wasReactivating ? "Certificate reactivated." : "Certificate updated.",
        variant: "success",
      });
    } catch (e: any) {
      setCertError(e.message ?? "Failed to save certificate.");
    } finally {
      setCertSaving(false);
    }
  };

  if (loading) return <div className="aro-page"><LoadingSpinner /></div>;
  if (error)   return <div className="aro-page"><div className="aro-error-banner">{error}</div></div>;
  if (!person) return null;

  const canEdit = canAccessQuarantine(changedBy);
  const canEditLi = canEditLinkedin(changedBy);
  const missingLinkedIn = !person.linkedin_url;
  const scrapeFailures  = person.consecutive_scrape_failures ?? 0;
  const scrapeReasonLabel = scrapeStatusReasonLabel(person.scrape_status_reason);
  const certificates = person.certificates ?? [];
  const snapshots    = person.snapshots    ?? [];
  const jobChanges   = person.job_changes  ?? [];

  return (
    <div className="aro-page">
      <Toast
        message={toast?.msg ?? null}
        variant={toast?.variant}
        onClose={() => setToast(null)}
      />

      <button className="person-back-btn" onClick={onBack}>← Back to People</button>

      <PageHeader
        title={person.full_name || "Person Detail"}
        right={
          missingLinkedIn
            ? <span className="person-linkedin-warning">LinkedIn URL missing — tracking disabled</span>
            : undefined
        }
      />

      {/* ── Two-column layout ─────────────────────────────── */}
      <div className="person-layout">

        {/* Left: Profile (editable for allowlisted email only) */}
        <div className="person-layout__left">
          {canEditLi && isEditing && draft ? (
            <section className="person-card">
              <h2 className="person-card__title">Edit Profile</h2>
              <div className="person-edit-grid">
                <div className="person-edit-row person-edit-row--full">
                  <label className="person-edit-label">Full Name</label>
                  <input
                    className="person-edit-input"
                    value={draft.full_name}
                    disabled={!canEdit}
                    onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                  />
                </div>
                <div className="person-edit-row person-edit-row--full">
                  <label className="person-edit-label">
                    LinkedIn URL
                    {!draft.linkedin_url && (
                      <span className="person-required-hint"> — required for tracking</span>
                    )}
                  </label>
                  <input
                    className={`person-edit-input ${!draft.linkedin_url ? "person-edit-input--required" : ""}`}
                    value={draft.linkedin_url}
                    placeholder="https://linkedin.com/in/..."
                    onChange={(e) => setDraft({ ...draft, linkedin_url: e.target.value })}
                  />
                  {!person.linkedin_url && draft.linkedin_url && (
                    <span className="person-li-hint">Saving will reactivate any paused certificates.</span>
                  )}
                </div>
              </div>
              {saveError && <p className="person-save-error">{saveError}</p>}
              <div className="person-card-actions">
                <button className="person-btn person-btn--ghost" onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button className="person-btn person-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </section>
          ) : (
            <section className="person-card">
              <div className="person-card__header">
                <h2 className="person-card__title">Profile</h2>
                {canEditLi && <button className="person-edit-btn" onClick={startEdit}>Edit</button>}
              </div>
              <div className="person-field-grid">
                <Field label="Full Name" value={person.full_name} />
                <Field label="Email"     value={person.email} />
                <div className={missingLinkedIn ? "person-linkedin-field--missing" : ""}>
                  <Field label="LinkedIn URL" value={person.linkedin_url} link />
                  {missingLinkedIn && (
                    <span className="person-linkedin-warning-inline">Required for tracking</span>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Right: Read-only details */}
        <div className="person-layout__right">
          <section className="person-card">
            <h2 className="person-card__title">Details</h2>
            <div className="person-field-grid">
              <Field label="Ingested Date"    value={formatDateLong(person.created_at)} />
              {person.current_company && (
                <Field label="Current Company" value={person.current_company} />
              )}
            </div>
            {scrapeFailures > 0 && (
              <div className={`person-failures-badge ${scrapeFailures >= 3 ? "person-failures-badge--critical" : ""}`}>
                ⚠ {scrapeFailures} consecutive scrape failure{scrapeFailures !== 1 ? "s" : ""}
                {scrapeFailures >= 3 && " — certificates likely paused"}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── Certificates ─────────────────────────────────── */}
      <section className="person-card person-card--full">
        <h2 className="person-card__title">
          Certificates
          <span className="person-card__count">{certificates.length}</span>
        </h2>
        {certificates.length === 0 ? (
          <p className="aro-muted">No certificates.</p>
        ) : (
          <div className="person-cert-table-wrap">
            <table className="aro-table">
              <thead>
                <tr>
                  <th>Certificate Name</th>
                  <th>Event Name</th>
                  <th>Certificate Earned Date</th>
                  <th>Actions</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((c, i) => {
                  const certTerminal  = isTerminal(c.tracking_status);
                  const isExpanded    = expandedCertId === c.id;
                  const isEditingThis = editingCertId === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <tr className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                        <td className="aro-table__cell--primary">{c.certificate_name}</td>
                        <td className="aro-muted">{c.event_name ?? "—"}</td>
                        <td>{formatDateLong(c.certificate_earned_date)}</td>
                        <td>
                          <div className="cert-actions-cell">
                            {canEdit && !certTerminal && canReactivate(c.tracking_status, c.tracking_status_reason) && (
                              <button
                                className="person-btn person-btn--primary person-btn--sm"
                                onClick={() => handleReactivate(c.id)}
                                disabled={reactivatingId === c.id}
                              >
                                {reactivatingId === c.id ? "…" : "Reactivate"}
                              </button>
                            )}
                            {canEditLi && !certTerminal && isNoLinkedinPause(c.tracking_status, c.tracking_status_reason) && (
                              <button
                                className="person-btn person-btn--ghost person-btn--sm"
                                onClick={startEdit}
                              >
                                + Add LinkedIn URL
                              </button>
                            )}
                            {canEdit && !certTerminal && (
                              <button
                                className="person-edit-btn"
                                onClick={() => startCertEdit(c)}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            className={`cert-expand-btn ${isExpanded ? "cert-expand-btn--open" : ""}`}
                            onClick={() => toggleExpand(c.id)}
                            title={isExpanded ? "Collapse" : "Show details"}
                          >
                            ▼
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && !isEditingThis && (
                        <tr>
                          <td colSpan={5} className="cert-expand-row">
                            <div className="cert-expand-panel">
                              <div className="cert-expand-grid">
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Status</span>
                                  {certStatusBadge(c.tracking_status, c.tracking_status_reason)}
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Certificate ID</span>
                                  <span className="cert-expand-value">{c.external_cert_id || "—"}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Status Reason</span>
                                  <span className="cert-expand-value">{stopReasonLabel(c.tracking_status_reason) || "—"}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Current LinkedIn Company</span>
                                  <span className="cert-expand-value">{c.baseline_company || "—"}</span>
                                </div>
                                {c.changed_company && (
                                  <div className="cert-expand-field">
                                    <span className="cert-expand-label">New Company</span>
                                    <span className="cert-expand-value">{c.changed_company}</span>
                                  </div>
                                )}
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Tracking End</span>
                                  <span className="cert-expand-value">{formatDateLong(c.tracking_end_date)}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Next LinkedIn Snapshot Due</span>
                                  <span className="cert-expand-value">{formatDateLong(c.next_snapshot_due)}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Inline cert edit panel */}
                      {canEdit && !certTerminal && isEditingThis && certDraft && (
                        <tr>
                          <td colSpan={5} className="person-cert-edit-row">
                            <div className="person-cert-edit-panel">
                              <div className="person-cert-edit-grid">
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Certificate Name</label>
                                  <input className="person-edit-input" value={certDraft.certificate_name}
                                    onChange={(e) => setCertDraft({ ...certDraft, certificate_name: e.target.value })} />
                                </div>
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Certificate ID</label>
                                  <input className="person-edit-input" value={certDraft.external_cert_id}
                                    placeholder="e.g. GC-AI-2026-00482"
                                    onChange={(e) => setCertDraft({ ...certDraft, external_cert_id: e.target.value })} />
                                </div>
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Certificate Earned Date</label>
                                  <input className="person-edit-input" type="date" value={certDraft.certificate_earned_date}
                                    onChange={(e) => setCertDraft({ ...certDraft, certificate_earned_date: e.target.value })} />
                                </div>
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Tracking End Date</label>
                                  <input className="person-edit-input" type="date" value={certDraft.tracking_end_date}
                                    onChange={(e) => setCertDraft({ ...certDraft, tracking_end_date: e.target.value })} />
                                </div>
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Next Snapshot Due</label>
                                  <input className="person-edit-input" type="date" value={certDraft.next_snapshot_due}
                                    onChange={(e) => setCertDraft({ ...certDraft, next_snapshot_due: e.target.value })} />
                                </div>
                                <div className="person-edit-row">
                                  <label className="person-edit-label">Tracking Status</label>
                                  <select className="person-edit-select" value={certDraft.tracking_status}
                                    onChange={(e) => setCertDraft({ ...certDraft, tracking_status: e.target.value as "active" | "paused" | "stopped" })}>
                                    <option value="active">Active</option>
                                    <option value="paused">Paused</option>
                                    <option value="stopped">Stopped</option>
                                  </select>
                                </div>
                              </div>
                              {certDraft.tracking_status === "active" &&
                                certificates.find((cc) => cc.id === editingCertId)?.tracking_status !== "active" && (
                                <p className="person-cert-reactivate-note">
                                  Setting status to Active will clear the stop reason and reset scrape failures.
                                </p>
                              )}
                              {certError && <p className="person-save-error">{certError}</p>}
                              <div className="person-card-actions">
                                <button className="person-btn person-btn--ghost" onClick={cancelCertEdit} disabled={certSaving}>Cancel</button>
                                <button className="person-btn person-btn--primary" onClick={handleCertSave} disabled={certSaving}>
                                  {certSaving ? "Saving…" : "Save Certificate"}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Snapshot Timeline ─────────────────────────────── */}
      <section className="person-card person-card--full">
        <h2 className="person-card__title">Snapshot Timeline</h2>
        {snapshots.length === 0 ? (
          <p className="aro-muted">No snapshots yet.</p>
        ) : (
          <div className="aro-timeline">
            {snapshots.map((snap: Snapshot, idx) => {
              const prev    = idx > 0 ? snapshots[idx - 1] : null;
              const changed = prev && snap.company !== prev.company;
              return (
                <div
                  key={snap.id}
                  className={`aro-timeline__node ${changed ? "aro-timeline__node--changed" : ""}`}
                >
                  <div className="aro-timeline__dot" />
                  <div className="aro-timeline__content">
                    <span className="aro-timeline__date">{formatDateLong(snap.snapshot_date)}</span>
                    <span className="aro-timeline__company">
                      {snap.company || "—"}
                      {snap.company_linkedin_url && (
                        <a
                          className="aro-external-link"
                          href={snap.company_linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          ↗
                        </a>
                      )}
                    </span>
                    {snap.source === "reused" && (
                      <AROBadge label="Cached" variant="gray" />
                    )}
                    {changed && <AROBadge label="Company changed" variant="green" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Job Changes ───────────────────────────────────── */}
      {jobChanges.length > 0 && (
        <section className="person-card person-card--full">
          <h2 className="person-card__title">Job Changes</h2>
          <div className="aro-job-changes">
            {jobChanges.map((jc) => (
              <div key={jc.id} className="aro-job-change">
                <span className="aro-job-change__route">
                  <span className="aro-job-change__old">
                    {jc.old_company || "—"}
                    {jc.old_company_linkedin_url && (
                      <a className="aro-external-link" href={jc.old_company_linkedin_url} target="_blank" rel="noreferrer">↗</a>
                    )}
                  </span>
                  <span className="aro-job-change__arrow">→</span>
                  <span className="aro-job-change__new">
                    {jc.new_company || "—"}
                    {jc.new_company_linkedin_url && (
                      <a className="aro-external-link" href={jc.new_company_linkedin_url} target="_blank" rel="noreferrer">↗</a>
                    )}
                  </span>
                </span>
                <span className="aro-job-change__date">{formatDateLong(jc.detected_date)}</span>
                <AROBadge
                  label={jc.within_window ? "Within Window" : "Outside Window"}
                  variant={jc.within_window ? "green" : "red"}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
